import { secondsToHumanReadable } from '../utils';

// import { Level } from "react-activity-calendar";
type Level = 0 | 1 | 2 | 3 | 4;

export interface Stat {
  description: string;
  count: number;
  date?: string;
  count_firsts?: number;
  days_since_last_message?: number;
  percentage?: number;
}

export interface Attachment {
  url: string;
  filename: string;
  type: 'image' | 'video' | 'other';
}

export interface Message {
  id: number;
  message_id: string;
  parent_id: number;
  first: boolean;
  subject: string;
  name: string;
  body: string;
  date: string;
  msg_date: string;
  has_attachment?: boolean;
  attachments?: Attachment[];
  replies?: Message[];
}

export interface User {
  name: string;
  unix_username?: string;
  birthday?: string;
  latitude?: number;
  longitude?: number;
  active: boolean;
  color?: string;
  admin: boolean;
  updated_at?: string;
  updated_by?: string;
  location_updated_at?: string;
}

export interface Column {
  header: string;
  isNumeric: boolean;
  value: (props: StatsValueProps) => string | number;
}

export interface StatDisplay {
  title: string;
  subheading?: string;
  columns: Column[];
  category?: 'Top' | 'Fun';
}

export interface StatsValueProps {
  stat: Stat;
  index: number;
  array: Stat[];
}

export interface Location {
  description: string;
  coordinates: Coordinates;
  icon: string;
  location_updated_at?: string;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface ChartPoint {
  date: string;
  data: ChartData[];
}

export interface ChartData {
  name: string;
  count: number;
}

export interface TimeOfDayValue {
  name: string;
  count: number;
  hour: number;
}

export type StatsValue = () => string;

export interface Heat {
  year: number;
  heat: HeatDate[];
}

export interface HeatDate {
  date: string;
  count: number;
  level: Level;
}

const defaultSubjectColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Subject',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: 'Posts',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'Δ',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.index === 0
        ? '0'
        : (props.stat.count - props.array[props.index - 1].count).toString(),
  },
];

const defaultPersonColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: 'Posts',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'Δ',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.index === 0
        ? '0'
        : (props.stat.count - props.array[props.index - 1].count).toString(),
  },
  {
    header: '🆕',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.stat.count_firsts ? props.stat.count_firsts.toString() : 'N/A',
  },
  {
    header: '% 🆕',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.stat.count_firsts
        ? props.stat.count_firsts === props.stat.count
          ? '100'
          : ((props.stat.count_firsts / props.stat.count) * 100.0)
              .toFixed(1)
              .toString()
        : 'N/A',
  },
];

const streakColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: 'Posts',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'When',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.date || '',
  },
];

const responseTimerColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: '~Response time',
    isNumeric: false,
    value: (props: StatsValueProps) => secondsToHumanReadable(props.stat.count),
  },
  {
    header: '# of replies',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.stat.count_firsts ? props.stat.count_firsts.toString() : '0',
  },
];

const swearStatsColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: 'Swears',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'Fav 🤬',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.date || '',
  },
];

const conversationStartersColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: '% of All Threads',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.stat.percentage ? props.stat.percentage.toFixed(2) + '%' : 'N/A',
  },
  {
    header: 'Threads Started',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'Replies',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.stat.count_firsts ? props.stat.count_firsts.toString() : '0',
  },
  {
    header: '% Threads vs Replies',
    isNumeric: true,
    value: (props: StatsValueProps) => {
      const total = props.stat.count + (props.stat.count_firsts || 0);
      return total > 0
        ? ((props.stat.count / total) * 100.0).toFixed(1).toString() + '%'
        : '0%';
    },
  },
];

const simpleCountColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: '%',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.stat.percentage ? props.stat.percentage.toFixed(2) + '%' : 'N/A',
  },
  {
    header: 'Count',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'Δ',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.index === 0
        ? '0'
        : (props.stat.count - props.array[props.index - 1].count).toString(),
  },
];

const avgLengthColumns = [
  {
    header: '',
    isNumeric: true,
    value: (props: StatsValueProps) => (props.index + 1).toString(),
  },
  {
    header: 'Who',
    isNumeric: false,
    value: (props: StatsValueProps) => props.stat.description,
  },
  {
    header: 'Avg Chars',
    isNumeric: true,
    value: (props: StatsValueProps) => props.stat.count.toString(),
  },
  {
    header: 'Δ',
    isNumeric: true,
    value: (props: StatsValueProps) =>
      props.index === 0
        ? '0'
        : (props.stat.count - props.array[props.index - 1].count).toString(),
  },
];

export const StatsAvailable: { [key: string]: StatDisplay } = {
  allTimeTopPosters: {
    title: 'All-time Top Posters',
    columns: defaultPersonColumns,
    category: 'Top',
  },
  thisYearTopPosters: {
    title: 'This Year Top Posters',
    columns: defaultPersonColumns,
    category: 'Top',
  },
  thisMonthTopPosters: {
    title: 'This Month Top Posters',
    columns: defaultPersonColumns,
    category: 'Top',
  },
  last100DaysTopPosters: {
    title: 'Last 100 days Top Posters',
    columns: defaultPersonColumns,
    category: 'Top',
  },
  last24HoursTopPosters: {
    title: 'Last 24h Top Posters',
    columns: defaultPersonColumns,
    category: 'Top',
  },
  todayTopPosters: {
    title: 'Today (Mtn time) Top Posters',
    columns: defaultPersonColumns,
    category: 'Top',
  },
  streaksByIndividual: {
    title: 'Top Uninterrupted Streaks by Poster',
    columns: streakColumns,
    category: 'Top',
  },
  streaksAllTime: {
    title: 'Top Uninterrupted Streaks (All-Time)',
    columns: streakColumns,
    category: 'Top',
  },
  word_count: {
    title: 'Word Usage / Search',
    columns: defaultPersonColumns.slice(0, 3),
  },
  topSubjects: {
    title: 'Top Subjects',
    columns: defaultSubjectColumns,
    category: 'Top',
  },
  swearStats: {
    title: 'Who Swears the Most? 🤬',
    subheading: 'Most frequent swearers',
    columns: swearStatsColumns,
    category: 'Fun',
  },
  conversationStarters: {
    title: 'Conversation Starters 💬',
    subheading: 'Most threads started',
    columns: conversationStartersColumns,
    category: 'Fun',
  },
  threadKillers: {
    title: 'Thread Killers 💀',
    subheading: 'Most times being the last person to post in a thread',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  conversationRevivers: {
    title: 'Conversation Revivers 🔄',
    subheading: 'Most times reviving a thread after 7 days of inactivity',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  replyMagnets: {
    title: 'Reply Magnets 🧲',
    subheading: 'Most replies received to their messages',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  dynamicDuos: {
    title: 'Dynamic Duos 👥',
    subheading: 'Total replies to each other',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  nightOwls: {
    title: 'Night Owls 🦉',
    subheading: 'People emailing between 12am and 5am',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  earlyBirds: {
    title: 'Early Birds 🐦',
    subheading: 'People emailing between 5am and 8am',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  hams: {
    title: 'The Hams 🍖',
    subheading: 'Highest average characters per message',
    columns: avgLengthColumns,
    category: 'Fun',
  },
  oneWordWonders: {
    title: 'One-Word Wonders 🤐',
    subheading: 'Most messages with less than 20 characters',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  attachmentFreaks: {
    title: 'Attachment Freaks 📎',
    subheading: 'Most messages with attachments',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  emilReplyStats: {
    title: 'Em(a)ils 🗣️',
    subheading: 'Replies to emails with vs without his name',
    columns: simpleCountColumns,
    category: 'Fun',
  },
  responseTimers: {
    title: 'Howdy Pardner 🤠',
    subheading: "Who's quickest to draw on who? (Last 100 days)",
    columns: responseTimerColumns,
    category: 'Fun',
  },
};
